import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Copy, Upload, Clock, Check, X, Image } from 'lucide-react';
import { depositApi, settingsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function UserDeposit() {
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [network, setNetwork] = useState('BEP20');
  const [blcPrice, setBlcPrice] = useState(0.027);
  const [formData, setFormData] = useState({
    amount: '',
    transaction_id: '',
    screenshot: ''
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, depositsRes] = await Promise.all([
        settingsApi.get(),
        depositApi.getAll()
      ]);
      setSettings(settingsRes.data.settings);
      setDeposits(depositsRes.data.deposits);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied!');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setScreenshotPreview(base64);
        setFormData({...formData, screenshot: base64});
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshotPreview(null);
    setFormData({...formData, screenshot: ''});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.transaction_id) {
      toast.error('Please fill amount and transaction ID');
      return;
    }

    if (!formData.screenshot) {
      toast.error('Please attach a screenshot of your transaction');
      return;
    }

    setSubmitting(true);
    try {
      await depositApi.create({
        amount: parseFloat(formData.amount),
        transaction_id: formData.transaction_id,
        network: network,
        screenshot: formData.screenshot
      });
      toast.success('Deposit submitted for approval!');
      setFormData({ amount: '', transaction_id: '', screenshot: '' });
      setScreenshotPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deposit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const currentAddress = network === 'BEP20' ? settings?.usdt_bep20_address : settings?.usdt_trc20_address;

  const depositFeePercent = settings?.deposit_fee || 0;
  const depositAmount = formData.amount ? parseFloat(formData.amount) : 0;
  const depositFeeAmount = depositAmount * (depositFeePercent / 100);
  const totalAmountToPay = depositAmount + depositFeeAmount;
  const usdToBLC = (usd) => blcPrice > 0 ? (usd / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="deposit-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-green-500/10">
          <Wallet className="w-6 h-6 text-green-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Deposit Funds</h1>
          <p className="text-sm text-zinc-500">Add USDT to your account</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Deposit Address Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6"
        >
          <h2 className="text-white font-medium mb-4">Payment Address</h2>
          
          {/* Network Selection */}
          <div className="mb-4">
            <label className="text-xs text-zinc-400 mb-2 block">Select Network</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setNetwork('BEP20')}
                className={`py-3 rounded-lg text-sm font-medium transition-all ${
                  network === 'BEP20' 
                    ? 'bg-amber-500 text-black' 
                    : 'glass text-zinc-400 hover:text-white'
                }`}
                data-testid="select-bep20"
              >
                USDT (BEP-20)
              </button>
              <button
                onClick={() => setNetwork('TRC20')}
                className={`py-3 rounded-lg text-sm font-medium transition-all ${
                  network === 'TRC20' 
                    ? 'bg-amber-500 text-black' 
                    : 'glass text-zinc-400 hover:text-white'
                }`}
                data-testid="select-trc20"
              >
                USDT (TRC-20)
              </button>
            </div>
          </div>

          {/* Address Display */}
          <div className="bg-black/30 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500">Deposit Address</span>
              <button 
                onClick={() => copyAddress(currentAddress)}
                className="text-amber-400 hover:text-amber-300"
                data-testid="copy-address-btn"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white font-mono text-xs break-all">
              {currentAddress || 'Not configured'}
            </p>
          </div>

          {/* QR Code from Settings */}
          <div className="bg-white rounded-lg p-4 flex items-center justify-center h-48">
            {(network === 'BEP20' && settings?.usdt_bep20_qr) || (network === 'TRC20' && settings?.usdt_trc20_qr) ? (
              <img 
                src={network === 'BEP20' ? settings.usdt_bep20_qr : settings.usdt_trc20_qr}
                alt={`${network} QR Code`}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="text-center">
                <div className="w-32 h-32 bg-zinc-100 mx-auto mb-2 flex items-center justify-center">
                  <span className="text-zinc-400 text-xs">QR Not Set</span>
                </div>
                <p className="text-xs text-zinc-600">Contact admin to set up QR</p>
              </div>
            )}
          </div>

          <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-200">
              <strong>Important:</strong> Only send USDT on the {network} network. Sending other tokens may result in permanent loss.
            </p>
          </div>

          {/* Deposit Rules */}
          {depositFeePercent > 0 && (
            <div className="mt-4 p-4 rounded-lg bg-black/30">
              <h3 className="text-white font-medium mb-3 text-sm">Deposit Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Deposit Fee</span>
                  <span className="text-amber-400">{depositFeePercent}%</span>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  The deposit fee will be added to your deposit amount. You'll need to pay the total amount shown in the summary.
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Deposit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6"
        >
          <h2 className="text-white font-medium mb-4">Submit Deposit</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Amount (USDT)</label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="Enter amount in USDT"
                className="bg-black/50 border-white/10 text-white"
                data-testid="deposit-amount"
                required
              />
              {depositAmount > 0 && (
                <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
                  <span className="text-zinc-500">Equivalent:</span> {usdToBLC(depositAmount)} BLC
                  <span className="text-zinc-600">@ ${blcPrice}/BLC</span>
                </p>
              )}
            </div>

            {/* Deposit Fee Summary */}
            {formData.amount && parseFloat(formData.amount) > 0 && (
              <div className="p-4 rounded-lg bg-black/30 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Deposit Amount</span>
                  <div className="text-right">
                    <span className="text-white">${depositAmount.toFixed(2)}</span>
                    <span className="text-amber-400 text-xs ml-2">({usdToBLC(depositAmount)} BLC)</span>
                  </div>
                </div>
                {depositFeePercent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Deposit Fee ({depositFeePercent}%)</span>
                    <span className="text-amber-400">+${depositFeeAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400 font-medium">Total Amount to Pay</span>
                  <div className="text-right">
                    <span className="text-green-400 font-medium">${totalAmountToPay.toFixed(2)}</span>
                    <span className="text-green-400/70 text-xs ml-2">({usdToBLC(totalAmountToPay)} BLC)</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400 font-medium">BLC Rate</span>
                  <span className="text-amber-400">${blcPrice} per BLC</span>
                </div>
                <p className="text-xs text-amber-200 mt-2">
                  Please send exactly ${totalAmountToPay.toFixed(2)} USDT to complete this deposit.
                </p>
              </div>
            )}

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Transaction ID / Hash</label>
              <Input
                type="text"
                value={formData.transaction_id}
                onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                placeholder="Enter transaction ID"
                className="bg-black/50 border-white/10 text-white"
                data-testid="deposit-txid"
                required
              />
            </div>

            {/* Screenshot Upload */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">
                Transaction Screenshot <span className="text-red-400">*</span>
              </label>
              
              {screenshotPreview ? (
                <div className="relative">
                  <img 
                    src={screenshotPreview} 
                    alt="Screenshot preview" 
                    className="w-full h-40 object-cover rounded-lg border border-white/10"
                  />
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="deposit-screenshot-input"
                  />
                  <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">Click to upload screenshot</p>
                  <p className="text-xs text-zinc-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full gold-btn"
              data-testid="submit-deposit"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                <>Submit Deposit</>
              )}
            </Button>
          </form>
        </motion.div>
      </div>

      {/* Deposit History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-4 sm:p-6"
      >
        <h2 className="text-white font-medium mb-4">Deposit History</h2>
        
        {deposits.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No deposits yet</p>
          </div>
        ) : (
          <>
            {/* Mobile View - Card Layout */}
            <div className="block sm:hidden space-y-3">
              {deposits.map((deposit) => (
                <div key={deposit.id} className="bg-black/30 rounded-lg p-4 border border-white/5">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-zinc-500">
                      {new Date(deposit.created_at).toLocaleDateString()}
                    </span>
                    <span className={`badge text-xs ${
                      deposit.status === 'approved' ? 'badge-success' :
                      deposit.status === 'rejected' ? 'badge-danger' : 'badge-warning'
                    }`}>
                      {deposit.status}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-500">Amount</span>
                      <div className="text-right">
                        <span className="text-white font-medium text-sm">${deposit.amount.toFixed(2)}</span>
                        <span className="text-amber-400 text-xs ml-1">({usdToBLC(deposit.amount)} BLC)</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-500">Fee</span>
                      <span className="text-amber-400 text-sm">{deposit.fee ? `+$${deposit.fee.toFixed(2)}` : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-500">Total</span>
                      <span className="text-green-400 font-medium text-sm">${(deposit.total_amount || deposit.amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-500">Network</span>
                      <span className="text-zinc-400 text-sm">{deposit.network}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop View - Table Layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Fee</th>
                    <th>Total Paid</th>
                    <th>Network</th>
                    <th>Transaction ID</th>
                    <th>Screenshot</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((deposit) => (
                    <tr key={deposit.id}>
                      <td className="text-zinc-400">
                        {new Date(deposit.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-white font-medium">
                        ${deposit.amount.toFixed(2)}
                        <span className="text-amber-400 text-xs ml-1">({usdToBLC(deposit.amount)} BLC)</span>
                      </td>
                      <td className="text-amber-400">
                        {deposit.fee ? `+$${deposit.fee.toFixed(2)}` : '-'}
                      </td>
                      <td className="text-green-400 font-medium">
                        ${(deposit.total_amount || deposit.amount).toFixed(2)}
                      </td>
                      <td className="text-zinc-400">{deposit.network}</td>
                      <td className="text-zinc-400 font-mono text-xs">
                        {deposit.transaction_id.slice(0, 15)}...
                      </td>
                      <td>
                        {deposit.screenshot ? (
                          <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center">
                            <Image className="w-4 h-4 text-green-400" />
                          </div>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          deposit.status === 'approved' ? 'badge-success' :
                          deposit.status === 'rejected' ? 'badge-danger' : 'badge-warning'
                        }`}>
                          {deposit.status === 'approved' && <Check className="w-3 h-3 mr-1" />}
                          {deposit.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                          {deposit.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {deposit.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
